import { getAccessToken } from "@/stores/authStore";

// ---------------------------------------------------------------------------
// API base URL — configured at build time via VITE_API_BASE_URL.
// In dev (Vite proxy) this is empty so /api/* is proxied to the backend.
// In production on Amplify, this is the backend URL.
// ---------------------------------------------------------------------------

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

function resolve(path: string): string {
  // If path already starts with the base, return as is
  if (API_BASE && path.startsWith(API_BASE)) return path;
  return `${API_BASE}${path}`;
}

// ---------------------------------------------------------------------------
// Base fetch wrapper
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, body: unknown) {
    const message =
      typeof body === "object" && body !== null && "error" in body
        ? String((body as unknown as Record<string, unknown>).error)
        : `Request failed (${status})`;
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function addAuthorizationHeader(headers: Record<string, string>): void {
  const token = getAccessToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
}

async function handleResponse(res: Response): Promise<unknown> {
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = { error: res.statusText };
    }
    throw new ApiError(res.status, body);
  }

  // 204 No Content
  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined;
  }

  return res.json();
}

// ---------------------------------------------------------------------------
// Verb helpers
// ---------------------------------------------------------------------------

function baseHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...extra,
  };
  addAuthorizationHeader(headers);
  return headers;
}

export async function apiGet<T = unknown>(
  path: string,
  params?: Record<string, string | number | boolean | undefined>,
): Promise<T> {
  let url = resolve(path);
  if (params) {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) sp.set(k, String(v));
    }
    const qs = sp.toString();
    if (qs) url += `?${qs}`;
  }

  const res = await fetch(url, {
    method: "GET",
    headers: baseHeaders(),
  });
  return (await handleResponse(res)) as T;
}

export async function apiPost<T = unknown>(
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(resolve(path), {
    method: "POST",
    headers: baseHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return (await handleResponse(res)) as T;
}

export async function apiPut<T = unknown>(
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(resolve(path), {
    method: "PUT",
    headers: baseHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return (await handleResponse(res)) as T;
}

export async function apiDelete<T = unknown>(
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(resolve(path), {
    method: "DELETE",
    headers: baseHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return (await handleResponse(res)) as T;
}

export async function apiPatch<T = unknown>(
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(resolve(path), {
    method: "PATCH",
    headers: baseHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return (await handleResponse(res)) as T;
}

// ---------------------------------------------------------------------------
// NDJSON streaming (used by Assistant)
// ---------------------------------------------------------------------------

export type StreamEventCallback = (event: unknown) => void;

/**
 * POST to a streaming endpoint that returns NDJSON. Parses each line and
 * calls `onEvent` for each parsed event. Returns when the stream is done.
 */
export async function postNdjsonStream(
  path: string,
  body: unknown,
  onEvent: StreamEventCallback,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(resolve(path), {
    method: "POST",
    headers: baseHeaders(),
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    let errBody: unknown;
    try {
      errBody = await res.json();
    } catch {
      errBody = { error: res.statusText };
    }
    throw new ApiError(res.status, errBody);
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error("Response body is not readable");

  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      // Keep the last (possibly incomplete) line in the buffer
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
          const parsed = JSON.parse(trimmed);
          onEvent(parsed);
        } catch {
          // Skip malformed lines
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export { addAuthorizationHeader, handleResponse };